/**
 * Denominational Documents View
 *
 * Allows users to search and browse official denominational documents
 * like Statement of Faith, Official Manuals, etc.
 *
 * Role-based display:
 * - Superusers: See "Denominational Documents" title and official documents only
 * - Regular users: See "Documents" title and their personal documents only
 */

import React, { useState, useEffect } from 'react';
import {
  searchDocuments,
  listDocuments,
  type DenominationalDocument,
  type SearchRequest
} from '../api/denominational-documents';
import MarkdownRenderer from './MarkdownRenderer';
import Pagination from './Pagination';
import ErrorBoundary from './ErrorBoundary';
import { useAuthStore } from '../stores/authStore';
import {
  parseApiError,
  showErrorNotification,
  retryOperation,
  isNetworkError,
  isServerError,
  logError,
} from '../utils/errorHandling';

interface DenominationalDocumentsViewProps {
  denominationId?: string; // Optional: filter by user's denomination
}

export const DenominationalDocumentsView: React.FC<DenominationalDocumentsViewProps> = ({
  denominationId
}) => {
  const { user } = useAuthStore();
  const [documents, setDocuments] = useState<DenominationalDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDocument, setSelectedDocument] = useState<DenominationalDocument | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [totalDocuments, setTotalDocuments] = useState(0);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [isSearchMode, setIsSearchMode] = useState(false);

  // Filter state
  const [filterDenomination, setFilterDenomination] = useState('');
  const [filterDocType, setFilterDocType] = useState('');
  const [filterDateRange, setFilterDateRange] = useState('all');
  const [filterTags, setFilterTags] = useState('');

  // Load documents on mount and when pagination changes
  useEffect(() => {
    if (isSearchMode) {
      // Perform search with pagination
      performSearch();
    } else {
      // Load documents with pagination
      loadDocuments();
    }
  }, [denominationId, currentPage, itemsPerPage]);

  const loadDocuments = async () => {
    setLoading(true);
    setError(null);

    try {
      const offset = (currentPage - 1) * itemsPerPage;

      // Use retry operation for network and server errors
      const response = await retryOperation(
        () => listDocuments(denominationId, undefined, itemsPerPage, offset),
        {
          maxRetries: 2,
          onRetry: (error, attempt) => {
            logError(error, `Loading documents (attempt ${attempt})`);
            if (attempt === 1) {
              setError('Having trouble loading documents. Retrying...');
            }
          },
        }
      );

      setDocuments(response.documents);
      setTotalDocuments(response.total);
      setError(null);
    } catch (err) {
      logError(err, 'Loading documents');

      // Parse and display user-friendly error message
      const errorMessage = parseApiError(err);

      if (isNetworkError(err)) {
        setError('Network error. Please check your internet connection and try again.');
      } else if (isServerError(err)) {
        setError('Server error. Please try again in a few moments.');
      } else {
        setError(errorMessage || 'Failed to load documents');
      }

      // Show toast notification as well
      showErrorNotification(err, errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const performSearch = async () => {
    if (!searchQuery.trim()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const offset = (currentPage - 1) * itemsPerPage;
      const request: SearchRequest = {
        query: searchQuery,
        denomination_id: denominationId,
        limit: itemsPerPage,
        offset: offset
      };

      // Use retry operation for network and server errors
      const response = await retryOperation(
        () => searchDocuments(request),
        {
          maxRetries: 2,
          onRetry: (error, attempt) => {
            logError(error, `Searching documents (attempt ${attempt})`);
            if (attempt === 1) {
              setError('Having trouble searching. Retrying...');
            }
          },
        }
      );

      setDocuments(response.documents);
      setTotalDocuments(response.total);
      setError(null);
    } catch (err) {
      logError(err, 'Searching documents');

      // Parse and display user-friendly error message
      const errorMessage = parseApiError(err);

      if (isNetworkError(err)) {
        setError('Network error. Please check your internet connection and try again.');
      } else if (isServerError(err)) {
        setError('Server error. Please try again in a few moments.');
      } else {
        setError(errorMessage || 'Search failed');
      }

      // Show toast notification as well
      showErrorNotification(err, errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!searchQuery.trim()) {
      setIsSearchMode(false);
      setCurrentPage(1);
      loadDocuments();
      return;
    }

    setIsSearchMode(true);
    setCurrentPage(1); // Reset to first page on new search
    performSearch();
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setFilterDenomination('');
    setFilterDocType('');
    setFilterDateRange('all');
    setFilterTags('');
    setIsSearchMode(false);
    setCurrentPage(1);
    loadDocuments();
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Scroll to top of document list
    const documentList = document.querySelector('.document-list-container');
    if (documentList) {
      documentList.scrollTop = 0;
    }
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const handleRetry = () => {
    if (isSearchMode && searchQuery.trim()) {
      performSearch();
    } else {
      loadDocuments();
    }
  };

  // Calculate total pages
  const totalPages = Math.ceil(totalDocuments / itemsPerPage);

  return (
    <div className="flex-1 flex flex-col h-full">
      {/* Compact Search & Filter Header */}
      <div className="p-3 sm:p-4 border-b border-dark-border">
        <h2 className="text-xl font-semibold mb-3">
          {/* Dynamic title based on user role */}
          {user?.is_superuser ? 'Denominational Documents' : 'Documents'}
        </h2>

          {/* Horizontal Filter Bar */}
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            {/* Search Input */}
            <div className="flex-1 min-w-[200px]">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch(e as any)}
                placeholder="Search documents..."
                className="w-full bg-dark-card border border-dark-border rounded-xl px-4 py-2 text-sm text-dark-text
                         focus:outline-none focus:border-accent-blue"
              />
            </div>

            {/* Denomination Filter - Only visible to superusers */}
            {user?.is_superuser && (
              <select
                value={filterDenomination}
                onChange={(e) => setFilterDenomination(e.target.value)}
                className="bg-dark-card border border-dark-border rounded-xl px-4 py-2 text-sm text-dark-text
                         focus:outline-none focus:border-accent-blue"
              >
                <option value="">All Denominations</option>
                <option value="cogic">COGIC</option>
                <option value="southern_baptist">Southern Baptist</option>
                <option value="catholic">Catholic</option>
                <option value="pentecostal">Pentecostal</option>
              </select>
            )}

            {/* Document Type Filter */}
            <select
              value={filterDocType}
              onChange={(e) => setFilterDocType(e.target.value)}
              className="bg-dark-card border border-dark-border rounded-xl px-4 py-2 text-sm text-dark-text
                       focus:outline-none focus:border-accent-blue"
            >
              <option value="">All Types</option>
              <option value="manual">Manual</option>
              <option value="statement_of_faith">Statement of Faith</option>
              <option value="constitution">Constitution</option>
              <option value="bylaws">Bylaws</option>
            </select>

            {/* Date Range Filter */}
            <select
              value={filterDateRange}
              onChange={(e) => setFilterDateRange(e.target.value)}
              className="bg-dark-card border border-dark-border rounded-xl px-4 py-2 text-sm text-dark-text
                       focus:outline-none focus:border-accent-blue"
            >
              <option value="all">All Time</option>
              <option value="7days">Last 7 Days</option>
              <option value="30days">Last 30 Days</option>
              <option value="6months">Last 6 Months</option>
              <option value="1year">Last Year</option>
            </select>

            {/* Tags Filter (Placeholder) */}
            <select
              value={filterTags}
              onChange={(e) => setFilterTags(e.target.value)}
              disabled
              title="Tag manager coming soon"
              className="bg-dark-card border border-dark-border rounded-xl px-4 py-2 text-sm text-dark-text-muted
                       opacity-50 cursor-not-allowed"
            >
              <option value="">Tags (Soon)</option>
            </select>

            {/* Clear Filters Button */}
            {(searchQuery || filterDenomination || filterDocType || filterDateRange !== 'all' || filterTags) && (
              <button
                onClick={handleClearSearch}
                className="bg-dark-card hover:bg-bridge-hover border border-dark-border text-white px-4 py-2 rounded-lg text-sm transition-colors whitespace-nowrap"
              >
                Clear
              </button>
            )}
          </div>

        {/* Results Count */}
        <p className="text-sm text-dark-text-muted">
          {totalDocuments} document{totalDocuments !== 1 ? 's' : ''} found
        </p>
      </div>

        {/* Error Message */}
        {error && (
          <div className="p-3 sm:p-4 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-2 flex-1">
                <svg
                  className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
              <button
                onClick={handleRetry}
                disabled={loading}
                className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700
                         disabled:opacity-50 disabled:cursor-not-allowed transition-colors
                         flex items-center gap-1"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                Retry
              </button>
            </div>
          </div>
        )}

      {/* Document List */}
      <div className="flex-1 overflow-y-auto document-list-container">
        {loading && documents.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="flex flex-col items-center gap-4">
                <div className="w-12 h-12 border-4 border-accent-blue border-t-transparent rounded-full animate-spin"></div>
                <div className="text-dark-text text-lg">Loading documents...</div>
              </div>
            </div>
          ) : documents.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor" className="w-20 h-20 text-dark-text-muted mb-4 opacity-40 mx-auto">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                </svg>
                <p className="text-lg font-semibold text-dark-text mb-2">
                  {searchQuery ? 'No documents found' : 'No documents yet'}
                </p>
                <p className="text-dark-text-muted max-w-md">
                  {searchQuery
                    ? 'Try adjusting your search or filters'
                    : denominationId
                      ? 'No documents available for your denomination yet'
                      : 'Documents will appear here once they are added'}
                </p>
              </div>
            </div>
          ) : (
            <div className="divide-y divide-dark-border">
              {loading && (
                <div className="absolute inset-0 bg-white/50 dark:bg-dark-bg/50 flex items-center justify-center z-10">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-blue"></div>
                </div>
              )}
              {documents.map((doc) => (
                <button
                  key={doc.id}
                  onClick={() => setSelectedDocument(doc)}
                  className={`w-full text-left p-3 sm:p-4 hover:bg-bridge-hover transition-colors
                            ${selectedDocument?.id === doc.id ? 'bg-accent-blue/10' : ''}`}
                  disabled={loading}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-medium text-dark-text">
                        {doc.section_title || `Section ${doc.section_number}` || doc.title}
                      </h3>
                      <p className="text-sm text-dark-text-muted mt-1">
                        {doc.citation_format || doc.title}
                      </p>
                    </div>
                    {doc.is_official && (
                      <span className="ml-2 px-2 py-0.5 text-xs bg-blue-100 dark:bg-blue-900/30
                                     text-blue-800 dark:text-blue-300 rounded">
                        Official
                      </span>
                    )}
                    {!doc.is_official && (
                      <span className="ml-2 px-2 py-0.5 text-xs bg-green-100 dark:bg-green-900/30
                                     text-green-800 dark:text-green-300 rounded">
                        Personal
                      </span>
                    )}
                  </div>
                  {doc.keywords && doc.keywords.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {doc.keywords.slice(0, 3).map((keyword, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-0.5 text-xs bg-dark-bg border border-dark-border
                                   text-dark-text-muted rounded"
                        >
                          {keyword}
                        </span>
                      ))}
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
      </div>

      {/* Pagination Controls */}
        {!loading || documents.length > 0 ? (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalDocuments}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            itemsPerPageOptions={[25, 50, 100]}
            loading={loading}
          />
        ) : null}

      {/* Document Detail Modal */}
      {selectedDocument && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4">
          <div className="bg-dark-card rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="p-3 sm:p-4 lg:p-6 border-b border-dark-border">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h1 className="text-2xl font-bold text-dark-text mb-2">
                    {selectedDocument.section_title || selectedDocument.title}
                  </h1>
                  <p className="text-sm text-dark-text-muted">
                    {selectedDocument.citation_format || selectedDocument.title}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedDocument(null)}
                  className="text-dark-text-muted hover:text-dark-text ml-4"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Document badges and keywords */}
              <div className="flex flex-wrap gap-2 mt-3">
                {selectedDocument.is_official ? (
                  <span className="px-3 py-1 text-sm bg-accent-blue/20 border border-accent-blue/30 text-accent-blue rounded-full">
                    Official Document
                  </span>
                ) : (
                  <span className="px-3 py-1 text-sm bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded-full">
                    Personal Document
                  </span>
                )}
                {selectedDocument.keywords && selectedDocument.keywords.slice(0, 5).map((keyword, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1 text-sm bg-dark-bg border border-dark-border text-dark-text-muted rounded-full"
                  >
                    {keyword}
                  </span>
                ))}
              </div>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-3 sm:p-4 lg:p-6">
              <div className="prose dark:prose-invert max-w-none">
                <MarkdownRenderer content={selectedDocument.content} />
              </div>

              {/* Document Metadata */}
              <div className="mt-8 pt-4 border-t border-dark-border">
                <dl className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <dt className="font-medium text-dark-text-muted">Document Type</dt>
                    <dd className="mt-1 text-dark-text capitalize">
                      {selectedDocument.document_type.replace(/_/g, ' ')}
                    </dd>
                  </div>
                  {/* Denomination - Only visible to superusers */}
                  {user?.is_superuser && (
                    <div>
                      <dt className="font-medium text-dark-text-muted">Denomination</dt>
                      <dd className="mt-1 text-dark-text uppercase">
                        {selectedDocument.denomination_id}
                      </dd>
                    </div>
                  )}
                  {selectedDocument.section_number && (
                    <div>
                      <dt className="font-medium text-dark-text-muted">Section Number</dt>
                      <dd className="mt-1 text-dark-text">
                        {selectedDocument.section_number}
                      </dd>
                    </div>
                  )}
                </dl>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Wrap the component with ErrorBoundary
const DenominationalDocumentsViewWithErrorBoundary: React.FC<DenominationalDocumentsViewProps> = (props) => {
  return (
    <ErrorBoundary
      onError={(error, errorInfo) => {
        logError(error, 'DenominationalDocumentsView ErrorBoundary');
        console.error('Error info:', errorInfo);
      }}
    >
      <DenominationalDocumentsView {...props} />
    </ErrorBoundary>
  );
};

export default DenominationalDocumentsViewWithErrorBoundary;
